import React, { useState, useEffect } from 'react';
import './Gestiondesdroits.css';

const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://demo.myssa.net/api';

const ROLES = ['Admin', 'Manager', 'Opérateur'];

// ★ Rôles : couleur + description (valeurs envoyées à l'API inchangées)
const ROLE_INFO = {
  Admin:     { tone: 'danger', court: 'Accès complet',            long: 'Accès complet — gestion des droits, toutes les pages' },
  Manager:   { tone: 'info',   court: 'Accès modules métier',     long: 'Accès aux modules métier — lecture et modification' },
  Opérateur: { tone: 'ok',     court: 'Consultation uniquement',  long: 'Accès limité — consultation uniquement' },
};

const MAX_TENTATIVES = 3;

// ── Petits composants ─────────────────────────────────────────────────────────

const RoleBadge = ({ role }) => (
  <span className={`gd-badge gd-badge-${ROLE_INFO[role]?.tone || 'neutral'}`}>{role}</span>
);

const Icone = ({ d, size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d.map((p, i) => <path key={i} d={p} />)}
  </svg>
);

const ICONES = {
  plus:    ['M12 5v14', 'M5 12h14'],
  close:   ['M6 6l12 12', 'M18 6L6 18'],
  unlock:  ['M7 11V7a5 5 0 0 1 9.9-1', 'M5 11h14v10H5z'],
  lock:    ['M7 11V7a5 5 0 0 1 10 0v4', 'M5 11h14v10H5z'],
  role:    ['M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z'],
  key:     ['M15 7a4 4 0 1 1-3.9 5H3v3h3v3h3v-3h2.1A4 4 0 0 1 15 7z'],
  mail:    ['M4 5h16v14H4z', 'M4 7l8 6 8-6'],
  trash:   ['M4 7h16', 'M9 7V4h6v3', 'M6 7l1 13h10l1-13'],
  check:   ['M5 12l5 5L20 7'],
  warn:    ['M12 3l10 18H2z', 'M12 10v4', 'M12 17h.01'],
};

const Modal = ({ title, sous, tone = 'navy', onClose, children, footer }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="gd-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="gd-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className={`gd-modal-head gd-modal-head-${tone}`}>
          <div>
            <div className="gd-modal-title">{title}</div>
            {sous && <div className="gd-modal-sub">{sous}</div>}
          </div>
          <button type="button" className="gd-modal-close" aria-label="Fermer" onClick={onClose}>
            <Icone d={ICONES.close} size={14} />
          </button>
        </div>
        <div className="gd-modal-body">{children}</div>
        {footer && <div className="gd-modal-foot">{footer}</div>}
      </div>
    </div>
  );
};

const Field = ({ label, id, ...props }) => (
  <div className="gd-field">
    <label className="gd-label" htmlFor={id}>{label}</label>
    <input id={id} className="gd-input" {...props} />
  </div>
);

const Toast = ({ msg, onClose }) => (
  <div className="gd-toast" role="status">
    <span className="gd-toast-icon"><Icone d={ICONES.check} size={12} /></span>
    {msg}
    <button type="button" className="gd-toast-close" aria-label="Fermer" onClick={onClose}>×</button>
  </div>
);

// Jauge de tentatives : 3 points
const Tentatives = ({ n }) => {
  const val = Number(n) || 0;
  const tone = val >= 2 ? 'danger' : val === 1 ? 'warn' : 'ok';
  return (
    <span className={`gd-tries gd-tries-${tone}`} title={`${val} tentative(s) échouée(s) sur ${MAX_TENTATIVES}`}>
      {Array.from({ length: MAX_TENTATIVES }).map((_, i) => (
        <span key={i} className={`gd-try ${i < val ? 'on' : ''}`} />
      ))}
      <span className="gd-tries-txt">{val} / {MAX_TENTATIVES}</span>
    </span>
  );
};

const initiales = (nom) => {
  const parts = String(nom || '?').split(/[.\s_-]+/).filter(Boolean);
  return ((parts[0]?.[0] || '?') + (parts[1]?.[0] || '')).toUpperCase();
};

// ── Composant principal ───────────────────────────────────────────────────────

const Gestiondesdroits = () => {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [toast, setToast]     = useState(null);
  const [roleFiltre, setRoleFiltre]   = useState('tous');
  const [bloquesSeuls, setBloquesSeuls] = useState(false);

  // Modals
  const [showCreate, setShowCreate]               = useState(false);
  const [showReset, setShowReset]                 = useState(null);
  const [showRole, setShowRole]                   = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);

  // Form créer
  const [newUser, setNewUser]         = useState({ username: '', email: '', role: 'Opérateur' });
  const [createError, setCreateError] = useState('');
  const [mailSentTo, setMailSentTo]   = useState(null);
  const [creating, setCreating]       = useState(false);

  // Form reset mdp
  const [resetError, setResetError] = useState('');

  useEffect(() => {
    document.title = 'MYSSA – Gestion des droits';
  }, []);

  const showMsg = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  // ── GET — Charger les utilisateurs ─────────────────────────────────────────
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/utilisateurs`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setUsers(data.map(u => ({
        id:         u.id,
        username:   u.username,
        email:      u.email,
        role:       u.role,
        bloque:     u.bloque,
        tentatives: u.tentatives,
        actif:      u.actif,
        client_id:  u.client_id,
      })));
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  // ── POST — Créer un utilisateur ────────────────────────────────────────────
  const handleCreate = async () => {
    setCreateError('');
    if (!newUser.username.trim()) { setCreateError("Le nom d'utilisateur est obligatoire."); return; }
    if (!newUser.email.trim() || !newUser.email.includes('@')) { setCreateError('Email invalide.'); return; }

    setCreating(true);
    try {
      const res = await fetch(`${API}/utilisateurs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: newUser.username, email: newUser.email, role: newUser.role }),
      });

      if (!res.ok) {
        const err = await res.json();
        setCreateError(err.error || "Erreur lors de la création ou de l'envoi du mail.");
        return;
      }

      await fetchUsers();
      const emailEnvoye = newUser.email;
      setShowCreate(false);
      setNewUser({ username: '', email: '', role: 'Opérateur' });
      setMailSentTo(emailEnvoye);
      setTimeout(() => setMailSentTo(null), 6000);
    } catch {
      setCreateError('Serveur inaccessible. Réessayez.');
    } finally {
      setCreating(false);
    }
  };

  // ── DELETE — Supprimer ─────────────────────────────────────────────────────
  const handleDelete = async (user) => {
    try {
      await fetch(`${API}/utilisateurs/${user.id}`, { method: 'DELETE' });
    } catch {}
    setUsers(prev => prev.filter(u => u.id !== user.id));
    setShowConfirmDelete(null);
    showMsg(`Utilisateur ${user.username} supprimé.`);
  };

  // ── PUT — Débloquer ────────────────────────────────────────────────────────
  const handleUnblock = async (user) => {
    try {
      await fetch(`${API}/utilisateurs/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bloque: false, tentatives: 0 }),
      });
    } catch {}
    setUsers(prev => prev.map(u => u.id === user.id ? { ...u, bloque: false, tentatives: 0 } : u));
    showMsg(`Compte de ${user.username} débloqué.`);
  };

  // ── PUT — Changer le rôle ──────────────────────────────────────────────────
  const handleRoleChange = async (user, newRole) => {
    try {
      await fetch(`${API}/utilisateurs/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
    } catch {}
    setUsers(prev => prev.map(u => u.id === user.id ? { ...u, role: newRole } : u));
    setShowRole(null);
    showMsg(`Rôle de ${user.username} changé en ${newRole}.`);
  };

  // ── POST — Renvoyer le lien d'invitation ───────────────────────────────────
  const handleResendLink = async (user) => {
    try {
      await fetch(`${API}/utilisateurs/${user.id}/resend-invite`, { method: 'POST' });
    } catch {}
    showMsg(`Lien renvoyé à ${user.email}.`);
  };

  // ── POST — Réinitialiser le mot de passe ───────────────────────────────────
  const handleResetPassword = async (user) => {
    setResetError('');
    try {
      await fetch(`${API}/utilisateurs/${user.id}/reset-password`, { method: 'POST' });
    } catch {}
    setShowReset(null);
    showMsg(`Un email de réinitialisation a été envoyé à ${user.email}.`);
  };

  // ── Filtres ────────────────────────────────────────────────────────────────
  const q = search.toLowerCase();
  const filtered = users
    .filter(u => (u.username || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
    .filter(u => roleFiltre === 'tous' || u.role === roleFiltre)
    .filter(u => !bloquesSeuls || u.bloque);

  const nbBloques = users.filter(u => u.bloque).length;
  const nbParRole = (r) => users.filter(u => u.role === r).length;

  const ouvrirCreation = () => { setShowCreate(true); setCreateError(''); };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="gd-page">
      {/* ★ En-têtes et données centrés dans le tableau (prioritaire sur Gestiondesdroits.css).
            Couvre aussi les blocs en flex : utilisateur (avatar + nom), boutons d'actions. */}
      <style>{`
        .gd-table th, .gd-table td { text-align: center !important; vertical-align: middle; }
        .gd-table .gd-user, .gd-table .gd-actions { justify-content: center !important; }
        .gd-table .gd-user-txt { align-items: center !important; text-align: center !important; }
      `}</style>
      <div className="gd-container">

        {toast && <Toast msg={toast} onClose={() => setToast(null)} />}

        {/* ── En-tête ── */}
        <div className="gd-header">
          <div className="gd-header-left">
            <div className="gd-title-row">
              <span className="gd-accent" />
              <h1 className="gd-title">Gestion des droits</h1>
              <span className="gd-chip-dept">ADMIN</span>
            </div>
       
          </div>
          <button type="button" className="gd-btn gd-btn-primary" onClick={ouvrirCreation}>
            <Icone d={ICONES.plus} size={12} /> Nouvel utilisateur
          </button>
        </div>

        {/* ── Bannière invitation envoyée ── */}
        {mailSentTo && (
          <div className="gd-banner">
            <span className="gd-banner-icon"><Icone d={ICONES.mail} size={14} /></span>
            <div>
              Un email de création de mot de passe a été envoyé à <strong>{mailSentTo}</strong>.
              <span className="gd-banner-sub"> L'utilisateur dispose de 24 h pour définir son mot de passe.</span>
            </div>
          </div>
        )}

        {/* ── Compteurs ── */}
        <div className="gd-kpis">
          <button
            type="button"
            className={`gd-kpi ${roleFiltre === 'tous' && !bloquesSeuls ? 'active' : ''}`}
            onClick={() => { setRoleFiltre('tous'); setBloquesSeuls(false); }}
          >
            <span className="gd-kpi-label">Utilisateurs</span>
            <span className="gd-kpi-value">{users.length}</span>
          </button>
          {ROLES.map(r => (
            <button
              key={r}
              type="button"
              className={`gd-kpi gd-kpi-${ROLE_INFO[r].tone} ${roleFiltre === r ? 'active' : ''}`}
              aria-pressed={roleFiltre === r}
              onClick={() => setRoleFiltre(roleFiltre === r ? 'tous' : r)}
            >
              <span className="gd-kpi-label"><span className="gd-kpi-dot" />{r}s</span>
              <span className="gd-kpi-value">{nbParRole(r)}</span>
            </button>
          ))}
          <button
            type="button"
            className={`gd-kpi gd-kpi-lock ${bloquesSeuls ? 'active' : ''} ${nbBloques ? 'gd-kpi-alert' : ''}`}
            aria-pressed={bloquesSeuls}
            onClick={() => setBloquesSeuls(b => !b)}
          >
            <span className="gd-kpi-label"><Icone d={ICONES.lock} size={11} /> Bloqués</span>
            <span className="gd-kpi-value">{nbBloques}</span>
          </button>
        </div>

        {/* ── Tableau ── */}
        <div className="gd-card gd-table-card">
          <div className="gd-table-head">
            <div className="gd-table-head-left">
              <span className="gd-table-title">Utilisateurs</span>
              <span className="gd-count">{filtered.length}{filtered.length !== users.length && <span className="gd-count-total"> / {users.length}</span>}</span>
              {roleFiltre !== 'tous' && (
                <button type="button" className="gd-filter-chip" onClick={() => setRoleFiltre('tous')}>{roleFiltre} <span aria-hidden="true">×</span></button>
              )}
              {bloquesSeuls && (
                <button type="button" className="gd-filter-chip gd-filter-danger" onClick={() => setBloquesSeuls(false)}>Bloqués <span aria-hidden="true">×</span></button>
              )}
            </div>
            <div className="gd-search">
              <svg className="gd-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
              </svg>
              <input
                className={`gd-input gd-input-search ${search ? 'active' : ''}`}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher un nom ou un email…"
                aria-label="Rechercher un utilisateur"
              />
              {search && <button type="button" className="gd-clear" aria-label="Effacer" onClick={() => setSearch('')}>×</button>}
            </div>
          </div>

          <div className="gd-table-scroll">
            {loading ? (
              <div className="gd-empty">Chargement…</div>
            ) : filtered.length === 0 ? (
              <div className="gd-empty">
                <div className="gd-empty-title">Aucun utilisateur trouvé</div>
                <div>Modifie la recherche ou les filtres.</div>
              </div>
            ) : (
              <table className="gd-table">
                <thead>
                  <tr>
                    <th>Utilisateur</th>
                    <th>Rôle</th>
                    <th>Statut</th>
                    <th>Tentatives</th>
                    <th className="gd-th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(user => (
                    <tr key={user.id} className={user.bloque ? 'gd-row-blocked' : ''}>
                      <td>
                        <div className="gd-user">
                          <span className={`gd-avatar gd-avatar-${ROLE_INFO[user.role]?.tone || 'neutral'}`}>{initiales(user.username)}</span>
                          <div className="gd-user-txt">
                            <span className="gd-user-name">{user.username}</span>
                            <span className="gd-user-mail">{user.email}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <RoleBadge role={user.role} />
                        <div className="gd-role-hint">{ROLE_INFO[user.role]?.court}</div>
                      </td>
                      <td>
                        {user.bloque
                          ? <span className="gd-status gd-status-danger"><Icone d={ICONES.lock} size={11} /> Bloqué</span>
                          : <span className="gd-status gd-status-ok"><span className="gd-status-dot" /> Actif</span>}
                      </td>
                      <td><Tentatives n={user.tentatives} /></td>
                      <td className="gd-td-actions">
                        <div className="gd-actions">
                          {user.bloque && (
                            <button type="button" className="gd-act gd-act-warn" onClick={() => handleUnblock(user)}>
                              <Icone d={ICONES.unlock} /> Débloquer
                            </button>
                          )}
                          <button type="button" className="gd-act" onClick={() => setShowRole(user)} title="Changer le rôle">
                            <Icone d={ICONES.role} /> Rôle
                          </button>
                          <button type="button" className="gd-act" onClick={() => { setShowReset(user); setResetError(''); }} title="Réinitialiser le mot de passe">
                            <Icone d={ICONES.key} /> Mot de passe
                          </button>
                          <button type="button" className="gd-act" onClick={() => handleResendLink(user)} title="Renvoyer le lien d'invitation">
                            <Icone d={ICONES.mail} /> Invitation
                          </button>
                          <button type="button" className="gd-act gd-act-danger gd-act-icon" onClick={() => setShowConfirmDelete(user)} title="Supprimer" aria-label={`Supprimer ${user.username}`}>
                            <Icone d={ICONES.trash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ── Modal : Créer utilisateur ── */}
        {showCreate && (
          <Modal
            title="Nouvel utilisateur"
            sous="Il recevra un email pour créer son mot de passe."
            onClose={() => setShowCreate(false)}
            footer={(
              <>
                <button type="button" className="gd-btn gd-btn-ghost" onClick={() => setShowCreate(false)}>Annuler</button>
                <button type="button" className="gd-btn gd-btn-primary" onClick={handleCreate} disabled={creating}>
                  {creating ? <><span className="gd-spinner" aria-hidden="true" /> Envoi…</> : <><Icone d={ICONES.mail} size={12} /> Créer et inviter</>}
                </button>
              </>
            )}
          >
            <div className="gd-modal-row">
              <Field label="Nom d'utilisateur" id="gd-username" type="text" value={newUser.username}
                onChange={e => setNewUser({ ...newUser, username: e.target.value })}
                placeholder="ex : jean.dupont" autoFocus />
              <Field label="Email" id="gd-email" type="email" value={newUser.email}
                onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
                placeholder="jean@entreprise.fr" />
            </div>

            <div className="gd-field">
              <span className="gd-label">Rôle</span>
              <div className="gd-role-choices" role="radiogroup" aria-label="Rôle">
                {ROLES.map(r => (
                  <button
                    key={r}
                    type="button"
                    role="radio"
                    aria-checked={newUser.role === r}
                    className={`gd-role-choice gd-role-${ROLE_INFO[r].tone} ${newUser.role === r ? 'active' : ''}`}
                    onClick={() => setNewUser({ ...newUser, role: r })}
                  >
                    <span className="gd-role-choice-top">
                      <span className="gd-radio" />
                      <RoleBadge role={r} />
                    </span>
                    <span className="gd-role-choice-txt">{ROLE_INFO[r].long}</span>
                  </button>
                ))}
              </div>
            </div>

            {createError && <div className="gd-alert gd-alert-danger"><Icone d={ICONES.warn} size={13} /> {createError}</div>}
          </Modal>
        )}

        {/* ── Modal : Réinitialiser mot de passe ── */}
        {showReset && (
          <Modal
            title="Réinitialiser le mot de passe"
            sous={showReset.username}
            onClose={() => setShowReset(null)}
            footer={(
              <>
                <button type="button" className="gd-btn gd-btn-ghost" onClick={() => setShowReset(null)}>Annuler</button>
                <button type="button" className="gd-btn gd-btn-primary" onClick={() => handleResetPassword(showReset)}>
                  <Icone d={ICONES.key} size={12} /> Envoyer le lien
                </button>
              </>
            )}
          >
            <p className="gd-text">
              Un email sera envoyé à <strong>{showReset.email}</strong> avec un lien pour définir un nouveau mot de passe.
            </p>
            <div className="gd-alert gd-alert-info">Le lien sera valable <strong>24 heures</strong>.</div>
            {resetError && <div className="gd-alert gd-alert-danger"><Icone d={ICONES.warn} size={13} /> {resetError}</div>}
          </Modal>
        )}

        {/* ── Modal : Changer rôle ── */}
        {showRole && (
          <Modal
            title="Changer le rôle"
            sous={showRole.username}
            onClose={() => setShowRole(null)}
            footer={<button type="button" className="gd-btn gd-btn-ghost" onClick={() => setShowRole(null)}>Annuler</button>}
          >
            <p className="gd-text">Rôle actuel : <RoleBadge role={showRole.role} /></p>
            <div className="gd-role-choices gd-role-choices-col">
              {ROLES.filter(r => r !== showRole.role).map(r => (
                <button
                  key={r}
                  type="button"
                  className={`gd-role-choice gd-role-choice-row gd-role-${ROLE_INFO[r].tone}`}
                  onClick={() => handleRoleChange(showRole, r)}
                >
                  <RoleBadge role={r} />
                  <span className="gd-role-choice-txt">{ROLE_INFO[r].long}</span>
                  <span className="gd-role-arrow" aria-hidden="true">→</span>
                </button>
              ))}
            </div>
          </Modal>
        )}

        {/* ── Modal : Confirmer suppression ── */}
        {showConfirmDelete && (
          <Modal
            title="Supprimer l'utilisateur"
            sous={showConfirmDelete.username}
            tone="danger"
            onClose={() => setShowConfirmDelete(null)}
            footer={(
              <>
                <button type="button" className="gd-btn gd-btn-ghost" onClick={() => setShowConfirmDelete(null)}>Annuler</button>
                <button type="button" className="gd-btn gd-btn-danger" onClick={() => handleDelete(showConfirmDelete)}>
                  <Icone d={ICONES.trash} size={12} /> Supprimer définitivement
                </button>
              </>
            )}
          >
            <p className="gd-text">
              Voulez-vous vraiment supprimer <strong>{showConfirmDelete.username}</strong> ({showConfirmDelete.email}) ?
            </p>
            <div className="gd-alert gd-alert-danger"><Icone d={ICONES.warn} size={13} /> Cette action est irréversible.</div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default Gestiondesdroits;